/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {PlanModelService} from './plan-model.service';
import {HttpClient} from '@angular/common/http';
import {AppConfigService} from './app-config.service';
import {isDefined} from './data-util';
import {
    ATTACHMENT_CSD,
    ATTACHMENT_PLAN_PDF,
    LAND_CSV,
    PDF_CREATION_METHOD,
    PDF_CREATION_METHOD_DESCRIPTION,
    PLAN_LODGEMENT_STAGE
} from '../constants';
import {AlertModalService, ConfirmationModalService} from '../component/modal';
import {catchError, EMPTY, first, map, Observable, of, tap} from 'rxjs';
import {NgEventBus} from 'ng-event-bus';
import {PermissionsService} from './permissions.service';
import {NotificationAlertsService} from '../notification-alerts/notification-alerts.service';
import {Router} from '@angular/router';
import {AttachmentValidationService} from './attachment-validation.service';
import {LookupService} from './lookup.service';
import {AttachmentTypeLookup} from '../models/attachment-type.model';
import {Customer, TitlesCustomer} from '../models/customer.model';
import {AttachmentConfiguration} from '../models/attachment-configuration.model';
import {CertificationType} from '../models/certification-type.model';
import {PlanAttachment} from '../models/plan-attachment.model';
import {switchMap} from 'rxjs/operators';
import {SpatialUtilService} from '../../design/services/spatial-util.service';
import {forkJoinDefaultIfEmpty} from '../observable.utils';
import {UiConfigurationService} from './ui-configuration.service';
import {DigitalPlanService} from '../../digital-plan/digital-plan.service';

@Injectable({
    providedIn: 'root'
})
export class PlanLodgementService {
    title = 'Plan Lodgement Service';

    private readonly permissions = ['VIEW_LODGE', 'EDIT_LODGE'];

    private calculatePlanLotsError = '';

    private customers: Customer[] = [];

    private attachmentConfigurations: AttachmentConfiguration[] = [];

    constructor(
        private planModelService: PlanModelService,
        private permissionsService: PermissionsService,
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private confirmationModalService: ConfirmationModalService,
        private eventBus: NgEventBus,
        private alertModalService: AlertModalService,
        private notificationAlertsService: NotificationAlertsService,
        private attachmentValidationService: AttachmentValidationService,
        private lookupService: LookupService,
        private router: Router,
        private spatialUtilService: SpatialUtilService,
        private uiConfigurationService: UiConfigurationService,
        private digitalPlanService: DigitalPlanService
    ) {}

    save(options?): Observable<any> {
        console.debug(`${this.title} - save`);
        options = options ? structuredClone(options) : {};
        options.reconcileSpatialFeatures = options.reconcileSpatialFeatures !== false; //Default to true when option not specified
        options.showWarningForCertifiedPlan = options.showWarningForCertifiedPlan !== false; //Default to true when option not specified
        options.showAlertOnSave = options.showAlertOnSave !== false; //Default to true when option not specified
        options.showAlertOnError = options.showAlertOnError !== false; //Default to true when option not specified

        if (options.showWarningForCertifiedPlan && this.isPlanCertified(this.planModelService.getModel())) {
            return this.showWarningForCertifiedPlan(this.planModelService.getModel()).pipe(
                switchMap(data => {
                    console.log(`${this.title} - save - showWarningForCertifiedPlan - action:${data}`);

                    let observable: Observable<any>;

                    if (data === 'plan-lodgement:remove-certifications') {
                        this.getModel().planLodgementStage = PLAN_LODGEMENT_STAGE.IN_DESIGN;
                        this.getModel().certifiedBy = undefined;
                        observable = this.removePlanPDFAttachment() ? this.updateAttachments(true) : of(null);
                    } else {
                        observable = of(null);
                    }

                    return observable.pipe(switchMap(() => this.save(Object.assign({}, options, {showWarningForCertifiedPlan: false}))));
                })
            );
        }

        // Capture whether this save makes an existing digital plan validation stale before savePlan() refreshes the
        // pristine model (via setModel). Compared against the last-saved values so a change only counts once committed.
        const validationStaleState = this.getDigitalPlanValidationStaleState();

        return this.planModelService.savePlan().pipe(
            map(data => {
                console.debug(`${this.title} - save - success!`);
                if (options.showAlertOnSave) {
                    this.notificationAlertsService.showPlanSaveSuccess();
                }
                return this.planModelService.setModel(data);
            }),
            switchMap(model => this.cancelStaleDigitalPlanValidation(validationStaleState, model)),
            tap(() => {
                if (options.reconcileSpatialFeatures) {
                    this.eventBus.cast('planLodgementService:plan-saved', true);
                }
            }),
            catchError(error => {
                const errorMsg = error.error && typeof error.error === 'object' ? error.error.message : '';
                console.error('Error saving plan. ' + errorMsg);
                this.alertModalService.createAlertMessage('Error saving plan. ' + errorMsg, 'Ok', 'Error saving plan', 'error');
                return EMPTY;
            })
        );
    }

    showWarningForCertifiedPlan(model) {
        const warningMessage =
            'All plan certification will be removed from ' +
            model.planType.name.toUpperCase() +
            ' ' +
            model.planNumber +
            '.  Please select Yes to continue';
        return this.confirmationModalService.showModal({
            title: 'Warning',
            confirmationText: warningMessage,
            action: 'plan-lodgement:remove-certifications'
        });
    }
    isPlanCertified(planSelected) {
        return (
            planSelected.planLodgementStage != null &&
            isDefined(planSelected.planLodgementStage) &&
            planSelected.planLodgementStage.toUpperCase() === PLAN_LODGEMENT_STAGE.CERTIFIED
        );
    }
    /**
     * Applies the model changes that make the plan a CAD plan, clearing the data that only applies while the plan's PDF is
     * produced from the data held for it. Shared by the create from CAD Software action and by attaching an externally
     * created Plan PDF, which converts the plan for the same reason.
     *
     * The plan returns to In Design because permissions are granted per lodgement stage and PDF creation method; a converted
     * plan left at a later stage has no pages its surveyor is permitted to work on, and no action available to recover with.
     *
     * The Plan PDF attachment is left to the caller, as one route deletes the PDF while the other has just attached one.
     */
    convertToCadPlan() {
        console.debug(`${this.title} - convertToCadPlan`);

        const model = this.getModel();

        model.pdfCreationMethod = PDF_CREATION_METHOD.CAD_SOFTWARE;
        model.planLodgementStage = PLAN_LODGEMENT_STAGE.IN_DESIGN;

        model.certifiedBy = undefined;
        model.certifyingSurveyor = undefined;
        model.certification = {variationApprovals: []};
        this.deriveCertificationType();
        this.deriveRegulatoryStatement();

        model.interests = [];
        model.partiallySurrenderedInterests = undefined;
        model.newInterestSameOwner = undefined;
        model.aggregateInterestQuestions = [];
        (model.formerTenures ?? []).forEach(formerTenure => (formerTenure.formerTenureNotifications = []));

        // The plan PDF is created outside the system from here on, so a declaration that it reflects the captured data cannot stand.
        model.planPdfReflectsDigitalCapturedData = undefined;

        delete model.digitalPlanAttachment;
    }

    /**
     * Clears the lodgement details entered for the plan as it was before a change of PDF creation method: the declarations
     * the surveyor made, and the lot counts and attachment validation results derived from a plan the lodgement no longer
     * holds. They are recalculated from the plan now attached, or re-entered, rather than carried over; a stale inset lot
     * count, for one, still adds to the chargeable lots.
     */
    resetLodgementDetails() {
        console.debug(`${this.title} - resetLodgementDetails`);

        const model = this.getModel();

        model.planConformanceAgreement = undefined;
        model.planReleaseConsent = undefined;

        model.planLots = 0;
        model.chargeablePlanLots = 0;
        model.insetLots = null;
        model.numberOfInsetLots = undefined;

        this.attachmentValidationService.clearAllValidationInfo();
    }

    /**
     * Sets the certification type and text from the plan's survey method. Left as they are when the survey method does not
     * determine a certification type, as it does not for plans whose survey details are not captured.
     */
    deriveCertificationType() {
        const model = this.getModel();

        if (!model.surveyMethod?.certificationlookupId) {
            return;
        }

        const certificationTypes = this.lookupService.getLookups<CertificationType>('certificationType');
        model.certificationType = certificationTypes[model.surveyMethod.certificationlookupId - 1];

        if (model.certificationType) {
            model.certification.certificationType = structuredClone(model.certificationType);
            model.certification.certificationText = model.certificationType.certificationText.replace(/<br>/g, '\n');
        }
    }

    /** Sets the survey regulatory statement from the plan's survey method, for the survey methods one applies to. */
    deriveRegulatoryStatement() {
        const model = this.getModel();

        if (model.surveyMethod?.regulatoryStatementApplies) {
            model.certification.surveyRegulatoryStatementApplies = model.surveyMethod.regulatoryStatementApplies;
            if (model.surveyMethod.regulatoryStatement) {
                model.certification.surveyRegulatoryStatement = model.surveyMethod.regulatoryStatement.replace(/<br>/g, '\n');
            }
        }
    }

    /**
     * Whether the plan's projected coordinate system can be used with the given PDF creation method. A plan that has no
     * coordinate system selected yet can always change method.
     */
    isCoordinateSystemValidForPdfCreationMethod(pdfCreationMethod: string): boolean {
        const projectedCoordSystem = this.getModel().spatialSettings?.projectedCoordSystem;
        return !projectedCoordSystem || (projectedCoordSystem.validPdfCreationMethods?.includes(pdfCreationMethod) ?? false);
    }

    /**
     * Informs the user that their coordinate system is invalid for the PDF creation method they are changing to.
     *
     * @param pdfCreationMethod The PDF creation method being changed to.
     */
    showInvalidCoordinateSystemMessage(pdfCreationMethod: string) {
        const coordinateSystem = this.getModel().spatialSettings.projectedCoordSystem;
        this.alertModalService.createAlertMessage(
            `The Projected coordinate system '${coordinateSystem.description}' cannot be used ` +
                `with the plan PDF creation method '${PDF_CREATION_METHOD_DESCRIPTION[pdfCreationMethod]}'. ` +
                `Please choose a suitable coordinate system before changing the plan PDF creation method.`,
            'Cancel'
        );
    }

    /**
     * Cancels any existing digital plan validation and notifies the Digital Plan tab to drop its cached copy. Called when a
     * plan stops being a Digital Plan, so the now-stale validation does not reappear if it becomes one again. A cancel
     * failure is swallowed, as it must not fail the change that has already been applied to the model.
     *
     * @returns whether a validation was cancelled.
     */
    cancelDigitalPlanValidation(): Observable<boolean> {
        return this.digitalPlanService.cancelLatestValidation().pipe(
            map(response => !!response),
            tap(cancelled => {
                if (cancelled) {
                    this.eventBus.cast('planLodgementService:digital-plan-validation-cleared');
                }
            }),
            catchError(error => {
                console.error(`${this.title} - cancelDigitalPlanValidation - error:`, error);
                return of(false);
            })
        );
    }

    increaseValidationAttempt() {
        console.debug(this.title + ': increaseValidationAttempt');
        if (isDefined(this.planModelService.getModel().attempt) && this.planModelService.getModel().attempt !== null) {
            this.planModelService.getModel().attempt += 1;
        }
    }
    remove(planLodgementId: number) {
        console.debug(this.title + ' remove');
        const url = this.appConfigService.settings.apiGatewayUrl + '/plans/lodgement/remove/' + planLodgementId;
        return this.http.post(url, {cache: false});
    }

    analysisApplicable() {
        const planPurpose = this.planModelService.getModel().planPurpose;
        return planPurpose && planPurpose.analysisUIButtonDisplayed !== undefined ? planPurpose.analysisUIButtonDisplayed : false;
    }

    certificationApplicable() {
        const {planPurpose, surveyMethod, pdfCreationMethod} = this.planModelService.getModel();
        const surveyMethodApplicable = this.isSurveyDetailsEnabled() ? !!surveyMethod : this.isDigitalPlan();
        return (planPurpose && surveyMethodApplicable && planPurpose.certificationPdfCreationMethods?.includes(pdfCreationMethod)) ?? false;
    }

    private isSurveyDetailsEnabled() {
        return this.uiConfigurationService.getInfoUiGroup()?.surveyDetailsDisplay ?? false;
    }

    designApplicable() {
        if (this.permissionsService.hasPermission(['EDIT_DESIGN', 'VIEW_DESIGN'])) {
            if (this.isDigitalPlan()) {
                return this.getModel().planPurpose?.pdfCreationMethods?.includes(PDF_CREATION_METHOD.DIGITAL_PLAN) ?? false;
            }
            if (this.isPortalPlan()) {
                return this.getModel().planPurpose?.pdfCreationMethods?.includes(PDF_CREATION_METHOD.SURVEYOR_PORTAL) ?? false;
            }
        }
        return false;
    }

    interestsApplicable() {
        const planPurpose = this.planModelService.getModel().planPurpose;

        if (!planPurpose) {
            return false;
        }

        if (this.isCadPlan()) {
            return planPurpose.userDefinedInterestsCADPlans ?? false;
        }

        if (this.isDigitalPlan()) {
            return planPurpose.userDefinedInterestsDigitalPlans ?? false;
        }

        if (this.isPortalPlan()) {
            return planPurpose.userDefinedInterestsPortalPlans ?? false;
        }

        return false;
    }

    hasMinimumPlanDetails() {
        return (
            isDefined(this.planModelService.getModel()) &&
            isDefined(this.planModelService.getModel().firm) &&
            this.planModelService.getModel().firm != null &&
            isDefined(this.planModelService.getModel().tenureType) &&
            this.planModelService.getModel().tenureType != null &&
            isDefined(this.planModelService.getModel().planType) &&
            this.planModelService.getModel().planType != null &&
            isDefined(this.planModelService.getModel().planNumber) &&
            this.planModelService.getModel().planNumber != null &&
            isDefined(this.planModelService.getModel().planPurpose) &&
            this.planModelService.getModel().planPurpose != null
        );
    }

    loadPlan(planLodgementId) {
        return this.planModelService.load(planLodgementId);
    }

    createNewPlanVersion(planLodgementId, lodgementType): Observable<any> {
        console.info(`${this.title} - createNewPlanVersion(${planLodgementId}, ${lodgementType})`);
        return this.planModelService.loadNewVersion(planLodgementId, lodgementType);
    }

    transitionStage(stage: string) {
        console.info(`${this.title} - transitionStage - stage=${stage}`);

        if (this.planModelService.modelIsDirty()) {
            return this.confirmationModalService
                .showModal({
                    title: 'Warning',
                    confirmationText: 'You are about to lose all changes. Are you sure you wish to continue?'
                })
                .pipe(
                    first(),
                    switchMap(action => {
                        if (action === 'yes') {
                            return this.planModelService.transitionStage(stage);
                        }
                        return of(null);
                    })
                );
        } else {
            return this.planModelService.transitionStage(stage);
        }
    }

    getPreviousVersionNumberLotCount(planLodgementId) {
        console.debug(`${this.title}: getPreviousVersionNumberLotCount`);
        return this.http.get(`${this.appConfigService.settings.apiGatewayUrl}/plans/lodgement/getPreviousVersionNumberLotCount/${planLodgementId}`);
    }

    cancel() {
        if (this.planModelService.modelIsDirty()) {
            this.confirmationModalService
                .showModal({
                    title: 'Warning',
                    confirmationText: 'You are about to lose all changes. Are you sure you wish to continue?'
                })
                .subscribe(action => {
                    if (action === 'yes') {
                        this.router.navigate(['/plan-dashboard']);
                    }
                });
        } else {
            this.router.navigate(['/plan-dashboard']);
        }
    }

    calculatePlanLots() {
        this.calculatePlanLotsError = '';

        if (this.isDigitalPlan()) {
            // planModel.planLots calculated and updated in DigitalPlanComponent#ngDoCheck
            return;
        }

        const model = this.planModelService.getModel();

        if (
            model.lodgementType?.value.toUpperCase() === 'AMENDMENT' &&
            !this.isPortalPlan() &&
            !this.attachmentValidationService.hasCSDorCSVAttachment() &&
            model.planPurpose &&
            model.planPurpose.id !== 'STPL_MERG_P2D2A'
        ) {
            return;
        }

        if (!model.planPurpose || model.planPurpose.maxNumberLotsCreated === 0) {
            model.planLots = 0;
            return;
        }

        if (this.isPortalPlan()) {
            model.planLots = this.spatialUtilService.getLots().length;
        } else if (model.attachments) {
            const attachments = model.attachments;

            const mandatoryAttachment = attachments.find(a => [ATTACHMENT_CSD, LAND_CSV].includes(a.attachmentType) && a.optionality === 'Mandatory');
            if (mandatoryAttachment?.fileName && this.attachmentValidationService.hasValidationInfo(mandatoryAttachment.attachmentType)) {
                const attachmentTypeTotalLotCount = this.attachmentValidationService.getTotalLotCount(mandatoryAttachment.attachmentType);
                const validationErrors = this.attachmentValidationService.getErrors(mandatoryAttachment.attachmentType);
                if (
                    validationErrors.some(
                        v =>
                            v.includes('the document must be in version 2.0 format') ||
                            v.includes('Invalid Lot Record CSV file, parsing error') ||
                            v.includes('lot numbers must be unique')
                    )
                ) {
                    model.planLots = 0;
                    const attachmentTypeLookups: AttachmentTypeLookup[] = this.lookupService.getLookups('attachmentType');
                    const attachmentTypeName = attachmentTypeLookups.find(v => v.id === mandatoryAttachment.attachmentType).name;
                    this.calculatePlanLotsError = `Error calculating lots. The attached ${attachmentTypeName} failed validation.`;
                } else if (typeof attachmentTypeTotalLotCount === 'number') {
                    model.planLots = attachmentTypeTotalLotCount;
                } else if (attachmentTypeTotalLotCount === null) {
                    model.planLots = 0;
                }
            } else if (
                attachments.find(a => a.attachmentType === ATTACHMENT_CSD && a.optionality === 'Optional') &&
                !attachments.find(a => a.attachmentType === LAND_CSV)
            ) {
                if (model.formerTenures[0]) {
                    model.planLots = model.formerTenures[0].lots.length;
                } else {
                    model.planLots = 0;
                    this.calculatePlanLotsError = 'Error calculating lots. The former tenure has not been entered.';
                }
            } else if (!model.planLots || (mandatoryAttachment && !mandatoryAttachment.fileName)) {
                model.planLots = 0;
            }
        } else {
            model.planLots = 0;
        }
    }

    getCalculatePlanLotsError() {
        return this.calculatePlanLotsError;
    }

    loadCustomers() {
        const logPrefix = `${this.title} - loadCustomers`;
        console.debug(logPrefix);

        const model = this.getModel();

        if (!model.firm && !model.surveyor) {
            console.debug(`${logPrefix} - surveyor details are not populated.`);
            return;
        }

        // set designation and identifier type
        const designation = model.firm?.firmNumber || model.surveyor?.registrationNumber;
        const identifierType = model.firm ? 'PLAN_FIRM_IDENTIFIER' : 'PLAN_SURVEYOR_IDENTIFIER';

        this.customers = JSON.parse(sessionStorage.getItem(`customer_${identifierType}_${designation}`)) || [];
        if (this.customers.length) {
            console.debug(`${logPrefix} - customers returned from local storage`);
            this.eventBus.cast('planLodgementService:customers', this.customers);
        } else {
            console.debug(`${logPrefix} - loading customer details for designation: ${designation}`);

            const url = `${this.appConfigService.settings.apiGatewayUrl}/customers/PLAN/${identifierType}/${designation}`;

            const subscription = this.http.get(url).subscribe({
                next: (titlesCustomers: TitlesCustomer[]) => {
                    console.debug(`${logPrefix} - success retrieving customer details from ${url}`);

                    if (titlesCustomers?.length > 0) {
                        titlesCustomers.forEach(titleCustomer => {
                            titleCustomer.customerUsages
                                .filter(customerUsage => customerUsage.usageType === 'PLAN')
                                .forEach(customerUsage => {
                                    customerUsage.customerUsageIdentifiers
                                        .filter(customerUsageIdentifier => customerUsageIdentifier.nameType === 'FINANCIAL_ACCOUNT')
                                        .forEach(customerUsageIdentifier => {
                                            this.customers.push({
                                                financialAccountNumber: parseInt(customerUsageIdentifier.designation),
                                                externalReference: titleCustomer.externalReference
                                            });
                                        });
                                });
                        });

                        this.eventBus.cast('planLodgementService:customers', this.customers);
                        sessionStorage.setItem(`customer_${identifierType}_${designation}`, JSON.stringify(this.customers));
                    } else {
                        console.error(`${logPrefix} - error populating customers - no data returned.`);

                        model.customerReference = undefined;

                        let name = '';

                        if (model.firm?.firmName) {
                            name = model.firm.firmName;
                        } else if (model.surveyor?.givenNames) {
                            name = `${model.surveyor.givenNames} ${model.surveyor.familyName} (${model.surveyor.registrationNumber})`;
                        }

                        this.alertModalService.createAlertMessage(
                            `There is no customer information set up for ${name} at Landgate. Please contact Landgate Customer Services.` +
                                'Lodgement will not be possible until this error is resolved. Landgate apologises for any inconvenience caused.'
                        );
                    }
                },
                error: error => {
                    console.error(`${logPrefix} - Error retrieving customers details from ${url}`, error);

                    model.customerReference = undefined;

                    this.alertModalService.createAlertMessage(
                        'There is an issue identifying customer and product information relevant to the plan to be lodged. Please contact ' +
                            'Landgate Customer Services. Lodgement will not be possible until this issue is resolved. Landgate apologises for ' +
                            'any inconvenience caused.'
                    );
                },
                complete: () => {}
            });

            this.eventBus.cast('nlr:busy', subscription);
        }
    }

    updateCustomersWithModelCustomer() {
        const modelCustomer = this.getModel().customer;
        if (!modelCustomer) {
            return;
        }

        const customerIndex = this.customers.findIndex(
            customer =>
                customer !== modelCustomer &&
                customer.financialAccountNumber === modelCustomer.financialAccountNumber &&
                customer.externalReference === modelCustomer.externalReference
        );

        if (customerIndex !== -1) {
            // Replace matching customer with model customer
            this.customers.splice(customerIndex, 1, modelCustomer);
            this.eventBus.cast('planLodgementService:customers', this.customers);
        }
    }

    getAttachments() {
        return this.getModel().attachments || [];
    }

    getAttachmentConfigurations() {
        return this.attachmentConfigurations;
    }

    updateAttachments(forceRefresh: boolean) {
        console.debug(`${this.title}: loadAttachmentConfigurations`);

        if (!this.getModel().planPurpose?.name) {
            console.debug('updateAttachments: Plan Purpose details have not been populated; no attachment model refresh will take place.');
            return of(null);
        }

        if (this.planModelService.readonly || !this.permissionsService.hasEditPermission(this.permissions)) {
            // Not refreshing attachment configuration, we're not able to edit the attachments anyway.
            this.sortAttachments();
            return of(null);
        }

        /**
         * We only refresh attachment configuration if we're in edit mode, otherwise merely viewing a plan will cause attachments that, say,
         * used to be 'Mandatory' (for this kind of plan), but the business has revised this to 'Prohibited' since it was last edited.
         *
         * The motivation for this is to not show unnecessary error messages that cannot be addressed. Perhaps a plan has already been lodged,
         * and one of its attachments had its configuration (with respect to plans) revised; it would not then be useful to show an attachment
         * as 'Prohibited'.
         */
        return this.http.post(`${this.getApiGatewayUrl()}/attachment/attachments`, this.planModelService.getModelForSerialisation()).pipe(
            tap((attachmentConfigurations: AttachmentConfiguration[]) =>
                this.refreshAttachmentConfigurationIfNeeded(attachmentConfigurations, forceRefresh)
            ),
            catchError((error: any) => {
                console.error(`Error retrieving attachments: ${error}`);
                this.alertModalService.createAlertMessage(
                    'Attachments could not be retrieved. Please try again and if the problem persists please contact Landgate Customer Services.'
                );
                return EMPTY;
            })
        );
    }

    /**
     * Adds empty attachment rows to the plan model if the plan lodgement now supports them.
     *
     * The supported attachments and the attachment rows (empty or populated) can be out-of-sync when a plan's purpose changes,
     * or when attachment configuration changes between user sessions (usually because a new release changes something); this
     * refresh resolves this, so that the user doesn't view an invalid list of attachment rows.
     *
     * @param attachmentConfigurations The attachment configurations.
     * @param forceRefresh If the method should always refresh the attachment rows.
     * Otherwise, we perform a check first, and only refresh if the check shows the attachment rows to be out of sync.
     */
    refreshAttachmentConfigurationIfNeeded(attachmentConfigurations: AttachmentConfiguration[], forceRefresh: boolean) {
        const logPrefix = `${this.title} - refreshAttachmentConfigurationIfNeeded`;
        console.debug(`${logPrefix} - attachment configurations:`, attachmentConfigurations);

        if (!forceRefresh && this.compareAttachmentConfigurations(attachmentConfigurations, this.attachmentConfigurations)) {
            console.debug(`${logPrefix} - not refreshing attachments as attachment configuration is unchanged and we are not forcing a refresh`);

            this.sortAttachments();
            return;
        }

        console.debug(`${logPrefix} - refreshing attachments as attachment configuration has changed.`);

        this.attachmentConfigurations = attachmentConfigurations;

        // Find all attachment types for which rows (empty or not) don't currently exist for, but are applicable, given this plan's configuration.
        const attachmentsToAdd = this.attachmentConfigurations.filter(ac => {
            return !this.getAttachments().some(attachment => ac.attachmentType === attachment.attachmentType);
        });

        if (!this.getModel().attachments) {
            this.getModel().attachments = [];
        }

        const newAttachments = attachmentsToAdd.map(attachment => ({
            attachmentType: attachment.attachmentType,
            optionality: attachment.optionality,
            createdByUser: false
        }));

        // Update any existing to change the optionality
        this.getAttachments().forEach(attachment => {
            const applicableAttachmentConfiguration = this.attachmentConfigurations.find(ac => attachment.attachmentType === ac.attachmentType);
            if (applicableAttachmentConfiguration) {
                attachment.optionality = applicableAttachmentConfiguration.optionality;
            }
        });

        // Find any we cant use anymore
        const validAttachmentTypes = this.attachmentConfigurations.map(ac => ac.attachmentType);

        const allAttachments = this.getAttachments().concat(newAttachments);

        if (!this.planModelService.readonly) {
            allAttachments
                .filter(attachment => !validAttachmentTypes.includes(attachment.attachmentType))
                .forEach(attachment => (attachment.optionality = 'Prohibited'));
        }

        // Remove any prohibited attachments
        this.getAttachments().splice(
            0,
            this.getAttachments().length,
            ...allAttachments.filter(attachment => attachment.optionality !== 'Prohibited' || attachment.fileName || attachment.attachmentDetails)
        );

        // Sort the attachments
        this.sortAttachments();

        // Validate CSD if the plan is not LODGED
        if (this.getModel().planLodgementStage !== 'LODGED') {
            // Do not show validation messages if the CSD hasn't changed since the previous version of the lodgement
            const subscription = this.http
                .get(`${this.getApiGatewayUrl()}/attachment/isAttachmentModified/${this.getModel().id}/Cadastral%20Survey%20Data`)
                .pipe(
                    switchMap((modified: boolean) => {
                        if (modified) {
                            this.attachmentValidationService.clearAllValidationInfo();
                            // Re-validate CSD attachments, so that we still display warnings
                            return forkJoinDefaultIfEmpty(
                                this.getAttachments()
                                    .filter(a => a.fileLocation && (a.attachmentType === ATTACHMENT_CSD || a.attachmentType === LAND_CSV))
                                    .map(a => this.attachmentValidationService.validate(a.fileLocation, a))
                            );
                        }
                        return of(null);
                    })
                )
                .subscribe(() => {
                    this.planModelService.updateAttachments();
                    this.eventBus.cast('REPOPULATE_FEES');
                });
            this.eventBus.cast('nlr:busy', subscription);
        }

        if (this.interestsApplicable()) {
            this.getModel().aggregateInterestQuestions = [];
        }
    }

    removePlanCSDAttachment() {
        return this.removePlanAttachment(ATTACHMENT_CSD);
    }

    removePlanPDFAttachment() {
        return this.removePlanAttachment(ATTACHMENT_PLAN_PDF);
    }

    private removePlanAttachment(attachmentType: string) {
        const attachments = this.getAttachments().filter(attachment => attachment.attachmentType === attachmentType && attachment.fileLocation);

        attachments.forEach((attachment: any) => {
            attachment.fileLocation = undefined;
            attachment.fileName = undefined;
            attachment.fileSize = undefined;
            attachment.attachmentDetails = undefined;
        });

        return attachments.length > 0;
    }

    private getModel() {
        return this.planModelService.getModel();
    }

    private getApiGatewayUrl() {
        return this.appConfigService.settings.apiGatewayUrl;
    }

    private isCadPlan() {
        return this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.CAD_SOFTWARE;
    }

    private isPortalPlan() {
        return this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.SURVEYOR_PORTAL;
    }

    private isDigitalPlan() {
        return this.getModel().pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN;
    }

    /**
     * Determines whether the pending save changes an attribute that makes an existing digital plan validation stale.
     * Compares the last-saved (pristine) values against the current model, so a change is only recognised once the
     * user commits it via save. Only applies when the plan was a Digital Plan before this save.
     *
     * @returns whether a validation is stale, and whether the change is limited to pdfCreationMethod (which
     * suppresses the warning, as it means the plan is deliberately no longer a Digital Plan).
     */
    private getDigitalPlanValidationStaleState(): {stale: boolean; pdfCreationMethodOnly: boolean} {
        const previous = this.planModelService.getPristineModel();
        const current = this.getModel();

        if (previous.pdfCreationMethod !== PDF_CREATION_METHOD.DIGITAL_PLAN) {
            return {stale: false, pdfCreationMethodOnly: false};
        }

        const pdfCreationMethodChanged = previous.pdfCreationMethod !== current.pdfCreationMethod;
        const otherAttributeChanged =
            previous.planNumber !== current.planNumber ||
            previous.planPurpose?.id !== current.planPurpose?.id ||
            previous.spatialSettings?.projectedCoordSystem?.id !== current.spatialSettings?.projectedCoordSystem?.id ||
            this.commonPropertyLotsKey(previous) !== this.commonPropertyLotsKey(current);

        return {
            stale: pdfCreationMethodChanged || otherAttributeChanged,
            pdfCreationMethodOnly: pdfCreationMethodChanged && !otherAttributeChanged
        };
    }

    /** The declared common property lots as a comparable key. Sorted, because only which lots were declared matters. */
    private commonPropertyLotsKey(model): string {
        return [...(model.digitalPlanAttachment?.commonPropertyLots ?? [])].sort((a, b) => a - b).join(',');
    }

    /**
     * Cancels the existing digital plan validation when the just-completed save made it stale, and shows a warning
     * unless the only change was pdfCreationMethod. Always passes the saved model through unchanged.
     */
    private cancelStaleDigitalPlanValidation(staleState: {stale: boolean; pdfCreationMethodOnly: boolean}, model): Observable<any> {
        if (!staleState.stale) {
            return of(model);
        }

        return this.cancelDigitalPlanValidation().pipe(
            tap(cancelled => {
                if (cancelled && !staleState.pdfCreationMethodOnly) {
                    this.notificationAlertsService.addMessage({
                        type: 'warning',
                        subType: 'digitalPlanValidationCleared',
                        text: 'Digital Plan validations were cleared due to plan changes.'
                    });
                }
            }),
            map(() => model)
        );
    }

    private sortAttachments() {
        this.getAttachments().sort((a: PlanAttachment, b: PlanAttachment) => {
            if (this.calculateOptionalitySortValue(a) > this.calculateOptionalitySortValue(b)) {
                return -1;
            }
            if (this.calculateOptionalitySortValue(a) < this.calculateOptionalitySortValue(b)) {
                return 1;
            }

            return a.attachmentType.localeCompare(b.attachmentType);
        });
    }

    private calculateOptionalitySortValue(attachment: PlanAttachment) {
        if (attachment.optionality === 'Prohibited') {
            return 300;
        }
        if (attachment.optionality === 'Mandatory') {
            return 200;
        } else {
            return 100;
        }
    }

    private compareAttachmentConfigurations(oldConfigs: AttachmentConfiguration[], newConfigs: AttachmentConfiguration[]) {
        if (!oldConfigs || !newConfigs) {
            return false;
        }

        if (oldConfigs.length !== newConfigs.length) {
            return false;
        }

        for (let i = 0; i < oldConfigs.length; i++) {
            const oldConfig = oldConfigs[i];
            const newConfig = newConfigs[i];

            if (!oldConfig || !newConfig) {
                return false;
            }

            if (oldConfig.attachmentType !== newConfig.attachmentType) {
                return false;
            }

            if (oldConfig.optionality !== newConfig.optionality) {
                return false;
            }

            if (oldConfig.multipleAttachments !== newConfig.multipleAttachments) {
                return false;
            }
        }
        return true;
    }
}
